import type { Layer } from '@geosoft/viewer-sdk';

/** One member of an RFC-108-G alternate group. */
export interface AlternateMember {
  layer: Layer;
  alternateOfLayerId: string | null;
  alternateLabel: string;
  alternatePreference: number | null;
}

/** Layer-panel row for a primary layer and its alternates. */
export interface AlternateGroup {
  groupKey: string;
  primary: Layer;
  members: AlternateMember[];
  displayLabel: string;
}

export function readAlternateOfLayerId(layer: Layer): string | null {
  if (layer.alternateOfLayerId === null) return null;
  if (typeof layer.alternateOfLayerId === 'string' && layer.alternateOfLayerId.length > 0) {
    return layer.alternateOfLayerId;
  }
  return null;
}

export function alternateGroupKey(layer: Layer): string {
  return readAlternateOfLayerId(layer) ?? layer.id;
}

function toAlternateMember(layer: Layer): AlternateMember {
  return {
    layer,
    alternateOfLayerId: readAlternateOfLayerId(layer),
    alternateLabel: layer.alternateLabel ?? layer.label ?? layer.id,
    alternatePreference:
      typeof layer.alternatePreference === 'number' && Number.isFinite(layer.alternatePreference)
        ? layer.alternatePreference
        : null,
  };
}

function sortMembersForDisplay(members: AlternateMember[]): AlternateMember[] {
  return [...members].sort((left, right) => {
    const leftPref = left.alternatePreference ?? Number.MAX_SAFE_INTEGER;
    const rightPref = right.alternatePreference ?? Number.MAX_SAFE_INTEGER;
    if (leftPref !== rightPref) return leftPref - rightPref;
    if (!left.alternateOfLayerId && right.alternateOfLayerId) return -1;
    if (left.alternateOfLayerId && !right.alternateOfLayerId) return 1;
    return left.alternateLabel.localeCompare(right.alternateLabel);
  });
}

/** Build one row per alternate group from catalog layers. */
export function buildAlternateGroups(layers: readonly Layer[]): AlternateGroup[] {
  const grouped = new Map<string, Layer[]>();
  for (const layer of layers) {
    const key = alternateGroupKey(layer);
    const bucket = grouped.get(key) ?? [];
    bucket.push(layer);
    grouped.set(key, bucket);
  }

  return [...grouped.entries()].map(([groupKey, bucket]) => {
    const members = sortMembersForDisplay(bucket.map(toAlternateMember));
    const primary =
      members.find((member) => !member.alternateOfLayerId)?.layer
      ?? members[0]?.layer
      ?? bucket[0];
    return {
      groupKey,
      primary,
      members,
      displayLabel: primary.label ?? primary.id,
    };
  });
}

/**
 * RFC-108-G §2.4 default-active selection:
 * 1. persisted visible member
 * 2. lowest alternatePreference
 * 3. primary (no alternateOfLayerId)
 */
export function selectDefaultActiveMember(
  members: readonly AlternateMember[],
  persistedVisibility: ReadonlyMap<string, boolean>,
): string {
  if (members.length === 0) {
    throw new Error('selectDefaultActiveMember requires at least one member');
  }

  const visibleMembers = members.filter((member) => persistedVisibility.get(member.layer.id) === true);
  if (visibleMembers.length === 1) return visibleMembers[0].layer.id;
  if (visibleMembers.length > 1) {
    return selectDefaultActiveMember(visibleMembers, new Map());
  }

  const withPreference = members.filter((member) => member.alternatePreference != null);
  if (withPreference.length > 0) {
    return [...withPreference].sort(
      (left, right) => left.alternatePreference! - right.alternatePreference!,
    )[0].layer.id;
  }

  const primary = members.find((member) => !member.alternateOfLayerId);
  return primary?.layer.id ?? members[0].layer.id;
}

export function groupMatchesQuery(group: AlternateGroup, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (group.displayLabel.toLowerCase().includes(q)) return true;
  if (group.groupKey.toLowerCase().includes(q)) return true;
  return group.members.some((member) => {
    const layer = member.layer;
    const label = (layer.label ?? '').toLowerCase();
    const id = layer.id.toLowerCase();
    const kind = layer.kind.toLowerCase();
    const alternateLabel = member.alternateLabel.toLowerCase();
    return label.includes(q) || id.includes(q) || kind.includes(q) || alternateLabel.includes(q);
  });
}

/** Resolve mutual exclusion and default-active ids when a scene loads. */
export function reconcileAlternateGroupVisibility(
  layers: readonly Layer[],
  visibilityEntries: ReadonlyArray<{ layerId: string; visible: boolean }>,
): {
  visibilityEntries: Array<{ layerId: string; visible: boolean }>;
  activeByGroup: Map<string, string>;
} {
  const groups = buildAlternateGroups(layers);
  const persistedVisibility = new Map(visibilityEntries.map((entry) => [entry.layerId, entry.visible]));
  const resolvedVisibility = new Map(visibilityEntries.map((entry) => [entry.layerId, entry.visible]));
  const activeByGroup = new Map<string, string>();

  for (const group of groups) {
    if (group.members.length <= 1) {
      activeByGroup.set(group.groupKey, group.primary.id);
      continue;
    }

    const activeId = selectDefaultActiveMember(group.members, persistedVisibility);
    activeByGroup.set(group.groupKey, activeId);
    const groupVisible = group.members.some((member) => persistedVisibility.get(member.layer.id) === true);

    for (const member of group.members) {
      resolvedVisibility.set(member.layer.id, groupVisible && member.layer.id === activeId);
    }
  }

  return {
    visibilityEntries: visibilityEntries.map((entry) => ({
      layerId: entry.layerId,
      visible: resolvedVisibility.get(entry.layerId) ?? entry.visible,
    })),
    activeByGroup,
  };
}

export function resolveActiveMemberId(
  group: AlternateGroup,
  visibleLayerIds: ReadonlySet<string>,
  activeByGroup: ReadonlyMap<string, string>,
): string {
  const tracked = activeByGroup.get(group.groupKey);
  if (tracked && group.members.some((member) => member.layer.id === tracked)) {
    return tracked;
  }

  const visibleMember = group.members.find((member) => visibleLayerIds.has(member.layer.id));
  if (visibleMember) return visibleMember.layer.id;

  return selectDefaultActiveMember(
    group.members,
    new Map(group.members.map((member) => [member.layer.id, visibleLayerIds.has(member.layer.id)])),
  );
}

export function isAlternateGroupVisible(
  _group: AlternateGroup,
  visibleLayerIds: ReadonlySet<string>,
  activeMemberId: string,
): boolean {
  return visibleLayerIds.has(activeMemberId);
}

/** Hide previous active alternate and show the newly selected one. */
export function applyAlternateSwitchVisibility(
  group: AlternateGroup,
  previousActiveId: string | undefined,
  nextActiveId: string,
  groupVisible: boolean,
): Array<{ layerId: string; visible: boolean }> {
  const updates: Array<{ layerId: string; visible: boolean }> = [];
  for (const member of group.members) {
    if (member.layer.id === previousActiveId) {
      updates.push({ layerId: member.layer.id, visible: false });
    } else if (member.layer.id === nextActiveId) {
      updates.push({ layerId: member.layer.id, visible: groupVisible });
    } else {
      updates.push({ layerId: member.layer.id, visible: false });
    }
  }
  return updates;
}
